import React, { useCallback, useEffect, useMemo, useState, useRef } from 'react';
import { AlertTriangleIcon, CalendarClockIcon, CheckCircle2Icon, ChevronRightIcon, CircleDotIcon, ClipboardIcon, LoaderCircleIcon, MessageCircleIcon, SendIcon, SkipForwardIcon } from 'lucide-react';
import { FollowUpScheduleError, cancelPendingFollowUp, completePendingFollowUp, createWorkflowActivity, isOverdue, loadNextAction, loadPendingFollowUp, remainingTime, retryFollowUpActivity, scheduleWorkflowFollowUp, updatePendingFollowUp, type NextAction, type RetryFollowUpActivityInput, type WorkflowActivityInput } from '../../lib/workflow';
import type { FollowUpRow } from '../../lib/supabase/types/database.types';
import { WorkflowActionModal } from './WorkflowActionModal';
type PendingActivityRetry = RetryFollowUpActivityInput;
type ProspectContact = {
  businessName: string;
  contactName: string | null;
  phone: string | null;
  whatsapp: string | null;
  email: string | null;
  instagram: string | null;
  facebook: string | null;
};
type WorkflowNextActionCardProps = {
  prospectId: string;
  prospect: ProspectContact;
  onRefresh?: () => Promise<void> | void;
  onUpdateInformation?: () => void;
};
type WorkdayState = {
  ids: string[];
  index: number;
};
const actionLabels: Record<string, string> = {
  complete_information: 'Preparar primer contacto',
  select_channel: 'Preparar primer contacto',
  send_message: 'Enviar primer mensaje',
  wait_for_response: 'Esperar respuesta',
  follow_up: 'Enviar seguimiento',
  respond: 'Registrar respuesta',
  qualify: 'Registrar respuesta',
  schedule_meeting: 'Agendar reunión',
  hold_meeting: 'Registrar resultado de reunión',
  send_proposal: 'Registrar propuesta enviada',
  negotiate: 'Registrar negociación',
  confirm_sale: 'Registrar venta',
  nurture: 'Programar contacto futuro',
  close_lost: 'Registrar motivo de pérdida',
  completed: 'Flujo completado'
};
export function WorkflowNextActionCard({
  prospectId,
  prospect,
  onRefresh,
  onUpdateInformation
}: WorkflowNextActionCardProps) {
  const [action, setAction] = useState<NextAction | null>(null);
  const [pendingFollowUp, setPendingFollowUp] = useState<FollowUpRow | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');
  const [activityType, setActivityType] = useState<string | null>(null);
  const [showPostpone, setShowPostpone] = useState(false);
  const [postponeReason, setPostponeReason] = useState('');
  const [postponeDueAt, setPostponeDueAt] = useState(defaultFollowUpDate());
  const [postponeChannel, setPostponeChannel] = useState('');
  const [pendingActivityRetry, setPendingActivityRetry] = useState<PendingActivityRetry | null>(null);
  const [isSaving, setIsSaving] = useState(false);
  const scheduleSubmissionRef = useRef(false);
  const workday = useMemo(() => readWorkday(), [prospectId]);
  const refresh = useCallback(async () => {
    setIsLoading(true);
    setError('');
    try {
      const [nextAction, pending] = await Promise.all([loadNextAction(prospectId), loadPendingFollowUp(prospectId)]);
      setAction(nextAction);
      setPendingFollowUp(pending);
    } catch (loadError) {
      setAction(null);
      setPendingFollowUp(null);
      setError(loadError instanceof Error ? loadError.message : 'No se pudo cargar la guía de venta.');
    } finally {
      setIsLoading(false);
    }
  }, [prospectId]);
  useEffect(() => {
    void refresh();
  }, [refresh]);
  async function refreshEverything(message?: string) {
    await refresh();
    await onRefresh?.();
    window.dispatchEvent(new Event('titan:workflow-updated'));
    if (message) setNotice(message);
  }
  async function completeActivity(input: WorkflowActivityInput) {
    await createWorkflowActivity(prospectId, {
      ...input,
      workflowActionType: effectiveWorkflowActionType(action?.actionType)
    });
    await refreshEverything('Acción registrada. El proceso comercial se actualizó con la información más reciente.');
    advanceWorkday();
  }
  function openPendingFollowUpEditor(followUp = pendingFollowUp) {
    if (!followUp) return;
    setPostponeDueAt(toDateTimeInputValue(followUp.scheduled_at));
    setPostponeChannel(followUp.channel);
    setPostponeReason(followUp.reason ?? '');
    setError('');
    setShowPostpone(true);
  }
  async function postpone(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    const selectedChannel = postponeChannel || channel.id;
    const requiresManualChannel = !pendingFollowUp && channel.id === 'manual';
    if (scheduleSubmissionRef.current || isSaving || !pendingFollowUp && !action?.stepCode || !postponeReason.trim() || !postponeDueAt || !selectedChannel || requiresManualChannel && !postponeChannel) return;
    scheduleSubmissionRef.current = true;
    const scheduledAt = new Date(postponeDueAt).toISOString();
    const retainedSchedule = {
      prospectId,
      scheduledAt,
      channel: selectedChannel,
      notes: postponeReason
    };
    setIsSaving(true);
    setError('');
    setNotice('');
    try {
      if (pendingFollowUp) {
        await updatePendingFollowUp({
          id: pendingFollowUp.id,
          scheduledAt,
          channel: selectedChannel,
          reason: postponeReason
        });
        setShowPostpone(false);
        await refreshEverything('Seguimiento actualizado correctamente.');
      } else {
        const scheduled = await scheduleWorkflowFollowUp({
          ...retainedSchedule,
          stepCode: action?.stepCode,
          recommendedChannel: channel.id === 'manual' ? undefined : channel.id
        });
        setPendingActivityRetry(null);
        setShowPostpone(false);
        await refreshEverything(scheduled.reusedFollowUp ? 'Ya existía un seguimiento pendiente. Abrimos ese seguimiento para editarlo.' : 'Seguimiento programado correctamente.');
        if (scheduled.reusedFollowUp) {
          const existing = await loadPendingFollowUp(prospectId);
          if (existing) {
            setPendingFollowUp(existing);
            openPendingFollowUpEditor(existing);
          }
        } else {
          setPostponeChannel('');
          setPostponeReason('');
        }
      }
    } catch (scheduleError) {
      if (isFollowUpActivityInsertError(scheduleError)) {
        setPendingActivityRetry(retainedSchedule);
        setError('');
      } else {
        setError(messageFrom(scheduleError, 'No se pudo completar la programación del seguimiento.'));
      }
    } finally {
      scheduleSubmissionRef.current = false;
      setIsSaving(false);
    }
  }
  async function completePending() {
    if (!pendingFollowUp || isSaving) return;
    setIsSaving(true);
    setError('');
    try {
      await completePendingFollowUp(pendingFollowUp.id);
      setPendingFollowUp(null);
      await refreshEverything('Seguimiento marcado como realizado.');
    } catch (completeError) {
      setError(messageFrom(completeError, 'No se pudo completar el seguimiento.'));
    } finally {
      setIsSaving(false);
    }
  }
  async function cancelPending() {
    if (!pendingFollowUp || isSaving) return;
    if (!window.confirm('¿Cancelar este seguimiento pendiente?')) return;
    setIsSaving(true);
    setError('');
    try {
      await cancelPendingFollowUp(pendingFollowUp.id);
      setPendingFollowUp(null);
      await refreshEverything('Seguimiento cancelado.');
    } catch (cancelError) {
      setError(messageFrom(cancelError, 'No se pudo cancelar el seguimiento.'));
    } finally {
      setIsSaving(false);
    }
  }
  async function retryActivity() {
    if (scheduleSubmissionRef.current || isSaving || !pendingActivityRetry) {
      return;
    }
    scheduleSubmissionRef.current = true;
    setIsSaving(true);
    setError('');
    setNotice('');
    try {
      await retryFollowUpActivity(pendingActivityRetry);
      setPendingActivityRetry(null);
      setShowPostpone(false);
      setPostponeChannel('');
      setPostponeReason('');
      await refreshEverything('Actividad de seguimiento registrada correctamente.');
    } catch (retryError) {
      setError(messageFrom(retryError, 'No se pudo registrar la actividad de seguimiento.'));
    } finally {
      scheduleSubmissionRef.current = false;
      setIsSaving(false);
    }
  }
  function advanceWorkday() {
    const state = readWorkday();
    if (!state) return;
    const nextIndex = state.index + 1;
    if (nextIndex >= state.ids.length) {
      sessionStorage.removeItem('titan-workday');
      setNotice('Jornada completada. No quedan acciones en esta cola.');
      return;
    }
    sessionStorage.setItem('titan-workday', JSON.stringify({
      ...state,
      index: nextIndex
    }));
    window.setTimeout(() => navigate(`/admin/prospectos/${state.ids[nextIndex]}`), 300);
  }
  function skipTemporarily() {
    setNotice('Acción saltada temporalmente. Sigue disponible en la cola comercial.');
    advanceWorkday();
  }
  if (isLoading) {
    return <section aria-busy="true" className="rounded-2xl border border-white/10 bg-[#0b1728] p-5 text-sm text-slate-400" data-id="element-681">
        <span className="inline-flex items-center gap-3" data-id="element-682">
          <LoaderCircleIcon className="h-5 w-5 animate-spin text-cyan-200" data-id="element-683" />
          Preparando guía de venta…
        </span>
      </section>;
  }
  if (error && !action) {
    return <section className="rounded-2xl border border-rose-400/20 bg-rose-400/10 p-5 text-sm text-rose-100" role="alert" data-id="element-684">
        <p data-id="element-685">{error}</p>
        <button className="mt-3 font-semibold underline underline-offset-4" onClick={() => void refresh()} type="button" data-id="element-686">
          Reintentar
        </button>
      </section>;
  }
  if (!action) {
    return <section className="rounded-2xl border border-rose-400/20 bg-rose-400/10 p-5 text-sm text-rose-100" role="alert" data-id="element-687">
        <p data-id="element-688">No se pudo determinar la siguiente acción.</p>
        <button className="mt-3 font-semibold underline underline-offset-4" onClick={() => void refresh()} type="button" data-id="element-689">
          Reintentar
        </button>
      </section>;
  }
  const isLegacyProfileAction = action.actionType === 'complete_information';
  const effectiveActionType = isLegacyProfileAction || action.actionType === 'select_channel' ? 'send_message' : action.actionType;
  const channel = getRecommendedChannel(prospect);
  const label = actionLabels[action.actionType] ?? action.actionLabel ?? 'Registrar acción';
  const title = isLegacyProfileAction ? 'Preparar primer contacto' : label;
  const expectedResult = expectedResultFor(effectiveActionType);
  const template = messageTemplate(prospect.businessName, prospect.contactName);
  const isWaitAction = action.workflowState === 'waiting';
  const isWorkflowCompleted = action.workflowState === 'completed';
  const isWorkflowBlocked = action.workflowState === 'blocked' || action.isBlocked;
  const overdue = isOverdue(action.dueAt);
  return <>
      <section aria-labelledby="sales-guide-title" className="overflow-hidden rounded-2xl border border-cyan-300/20 bg-[#0b1728] shadow-sm" data-id="element-690">
        <div className="border-b border-cyan-300/15 bg-[#0d1d31] px-5 py-4 sm:px-6" data-id="element-691">
          <div className="flex flex-wrap items-center justify-between gap-3" data-id="element-692">
            <p className="font-orbitron text-xs font-bold tracking-[.18em] text-cyan-200" data-id="element-693">
              GUÍA DE VENTA
            </p>
            <span className="rounded-full border border-cyan-300/20 bg-cyan-300/10 px-3 py-1 text-xs font-bold text-cyan-100" data-id="element-694">
              {action.stepNumber && action.totalSteps ? `Paso ${action.stepNumber} de ${action.totalSteps}` : workflowStateLabel(action.workflowState)}
            </span>
          </div>
          <h2 className="mt-3 text-2xl font-bold text-white" id="sales-guide-title" data-id="element-695">
            {title}
          </h2>
        </div>

        <div className="p-5 sm:p-6" data-id="element-696">
          {workday ? <p className="mb-5 rounded-xl border border-cyan-300/20 bg-cyan-300/10 px-3 py-2 text-sm font-semibold text-cyan-100" data-id="element-697">
              Jornada activa: {Math.min(workday.index + 1, workday.ids.length)}{' '}
              de {workday.ids.length} acciones
            </p> : null}

          <div className="grid gap-4 border-b border-white/10 pb-5 sm:grid-cols-2" data-id="element-698">
            <GuideItem label="Qué hacer ahora" value={instructionFor(effectiveActionType, prospect.businessName)} data-id="element-699" />
            <GuideItem label="Por qué" value={action.description || rationaleFor(effectiveActionType)} data-id="element-700" />
            <GuideItem label="Cómo hacerlo" value={howToFor(effectiveActionType, channel.label)} data-id="element-701" />
            <GuideItem label="Resultado esperado" value={expectedResult} data-id="element-702" />
          </div>

          {effectiveActionType === 'send_message' || effectiveActionType === 'follow_up' ? <div className="mt-5 rounded-xl border border-white/10 bg-[#07111f] p-4" data-id="element-703">
              <p className="flex items-center gap-2 text-sm font-bold text-cyan-100" data-id="element-704">
                <MessageCircleIcon className="h-4 w-4" data-id="element-705" />
                Canal recomendado: {channel.label}
              </p>
              <p className="mt-3 whitespace-pre-line text-sm leading-6 text-slate-300" data-id="element-706">
                {template}
              </p>
              <button className="mt-3 inline-flex items-center gap-2 text-sm font-semibold text-cyan-200 underline underline-offset-4 focus:outline-none focus-visible:ring-2 focus-visible:ring-cyan-300" onClick={() => void copyToClipboard(template, setNotice)} type="button" data-id="element-707">
                <ClipboardIcon className="h-4 w-4" data-id="element-708" />
                Copiar mensaje
              </button>
            </div> : null}

          {isWaitAction ? <div className="mt-5 rounded-xl border border-amber-300/20 bg-amber-300/10 p-4 text-sm text-amber-50" data-id="element-709">
              <div className="flex gap-3" data-id="element-710">
                <CalendarClockIcon className="mt-0.5 h-5 w-5 shrink-0 text-amber-200" data-id="element-711" />
                <div data-id="element-712">
                  <p className="font-bold" data-id="element-713">
                    Esperá 48 horas antes de retomar el contacto.
                  </p>
                  <p className="mt-1 leading-6 text-amber-100" data-id="element-714">
                    Programá el seguimiento recomendado para no perder el ritmo
                    comercial.
                  </p>
                  <p className="mt-2 font-semibold text-amber-100" data-id="element-715">
                    {action.dueAt ? `Próxima fecha: ${formatDueAt(action.dueAt)} · ${remainingTime(action.dueAt)}` : 'Todavía no hay una fecha de seguimiento definida.'}
                  </p>
                </div>
              </div>
            </div> : null}

          {isLegacyProfileAction ? <p className="mt-5 rounded-xl border border-white/10 bg-white/[0.03] p-3 text-sm text-slate-300" data-id="element-716">
              La información disponible es suficiente para iniciar la
              conversación.{' '}
              <button className="font-semibold text-cyan-200 underline underline-offset-4 focus:outline-none focus-visible:ring-2 focus-visible:ring-cyan-300" onClick={onUpdateInformation} type="button" data-id="element-717">
                Actualizar información
              </button>
            </p> : null}

          {notice ? <p className="mt-4 rounded-lg border border-emerald-300/20 bg-emerald-300/10 p-3 text-sm text-emerald-100" role="status" data-id="element-718">
              {notice}
            </p> : null}
          {pendingFollowUp ? <section aria-label="Seguimiento pendiente" className="mt-4 rounded-xl border border-amber-300/25 bg-amber-300/10 p-4 text-sm text-amber-50" data-id="element-719">
              <p className="font-bold" data-id="element-720">Seguimiento pendiente</p>
              <p className="mt-1 text-amber-100" data-id="element-721">
                Fecha y hora: {formatDueAt(pendingFollowUp.scheduled_at)}
              </p>
              <p className="mt-1 text-amber-100" data-id="element-722">
                Canal: {channelLabel(pendingFollowUp.channel)}
              </p>
            </section> : null}
          {pendingActivityRetry ? <section className="mt-4 rounded-lg border border-amber-300/25 bg-amber-300/10 p-3 text-sm text-amber-50" role="status" data-id="element-723">
              <p className="font-semibold" data-id="element-724">El seguimiento ya está guardado.</p>
              <p className="mt-1 text-amber-100" data-id="element-725">
                Solo falta registrar la actividad. No se volverá a crear el
                seguimiento.
              </p>
            </section> : null}
          {error ? <p className="mt-4 rounded-lg border border-rose-400/20 bg-rose-400/10 p-3 text-sm text-rose-100" role="alert" data-id="element-726">
              {error}
            </p> : null}

          <div className="mt-6 flex flex-col gap-2 sm:flex-row sm:flex-wrap" data-id="element-727">
            {isWorkflowCompleted ? <span className="inline-flex items-center gap-2 rounded-xl bg-emerald-300/10 px-4 py-2.5 text-sm font-bold text-emerald-100" data-id="element-728">
                <CheckCircle2Icon className="h-4 w-4" data-id="element-729" />
                Flujo completado
              </span> : isWorkflowBlocked ? <span className="inline-flex items-center gap-2 rounded-xl border border-rose-400/25 bg-rose-400/10 px-4 py-2.5 text-sm font-bold text-rose-100" data-id="element-730">
                <AlertTriangleIcon className="h-4 w-4" data-id="element-731" />
                Proceso bloqueado
              </span> : pendingActivityRetry ? <button className="inline-flex items-center justify-center gap-2 rounded-xl bg-amber-300 px-4 py-2.5 text-sm font-bold text-[#382500] hover:bg-amber-200 disabled:cursor-not-allowed disabled:opacity-60" disabled={isSaving} onClick={() => void retryActivity()} type="button" data-id="element-732">
                {isSaving ? <LoaderCircleIcon className="h-4 w-4 animate-spin" data-id="element-733" /> : null}
                {isSaving ? 'Guardando actividad…' : 'Reintentar actividad'}
              </button> : pendingFollowUp ? <>
                <button className="inline-flex items-center justify-center gap-2 rounded-xl bg-cyan-300 px-4 py-2.5 text-sm font-bold text-[#06202a] hover:bg-cyan-200 disabled:cursor-not-allowed disabled:opacity-60" disabled={isSaving} onClick={openPendingFollowUpEditor} type="button" data-id="element-734">
                  <CalendarClockIcon className="h-4 w-4" data-id="element-735" />
                  Editar seguimiento
                </button>
                <button className="inline-flex items-center justify-center gap-2 rounded-xl border border-emerald-300/30 px-4 py-2.5 text-sm font-bold text-emerald-100 hover:bg-emerald-300/10 disabled:opacity-60" disabled={isSaving} onClick={() => void completePending()} type="button" data-id="element-736">
                  <CheckCircle2Icon className="h-4 w-4" data-id="element-737" />
                  Marcar realizado
                </button>
                <button className="inline-flex items-center justify-center gap-2 rounded-xl border border-rose-400/30 px-4 py-2.5 text-sm font-bold text-rose-100 hover:bg-rose-400/10 disabled:opacity-60" disabled={isSaving} onClick={() => void cancelPending()} type="button" data-id="element-738">
                  Cancelar seguimiento
                </button>
              </> : isWaitAction ? <>
                <button className="inline-flex items-center justify-center gap-2 rounded-xl bg-cyan-300 px-4 py-2.5 text-sm font-bold text-[#06202a] hover:bg-cyan-200 disabled:cursor-not-allowed disabled:opacity-60" disabled={isSaving} onClick={() => setShowPostpone(true)} type="button" data-id="element-739">
                  <CalendarClockIcon className="h-4 w-4" data-id="element-740" />
                  Programar seguimiento
                </button>
                <button className="inline-flex items-center justify-center gap-2 rounded-xl border border-white/15 px-4 py-2.5 text-sm font-bold text-slate-200 hover:bg-white/5" onClick={() => setActivityType('wait_for_response')} type="button" data-id="element-741">
                  <CircleDotIcon className="h-4 w-4" data-id="element-742" />
                  Registrar respuesta
                </button>
              </> : <button className="inline-flex items-center justify-center gap-2 rounded-xl bg-cyan-300 px-4 py-2.5 text-sm font-bold text-[#06202a] hover:bg-cyan-200" onClick={() => setActivityType(effectiveActionType)} type="button" data-id="element-743">
                <SendIcon className="h-4 w-4" data-id="element-744" />
                {primaryLabelFor(effectiveActionType)}
                <ChevronRightIcon className="h-4 w-4" data-id="element-745" />
              </button>}

            {overdue ? <span className="inline-flex items-center gap-1 rounded-xl border border-rose-400/25 px-3 py-2 text-sm font-semibold text-rose-100" data-id="element-746">
                <AlertTriangleIcon className="h-4 w-4" data-id="element-747" />
                {remainingTime(action.dueAt)}
              </span> : null}

            {workday && !pendingActivityRetry && !isWorkflowCompleted && !isWorkflowBlocked ? <button className="inline-flex items-center justify-center gap-2 rounded-xl border border-white/15 px-4 py-2.5 text-sm font-bold text-slate-200 hover:bg-white/5" onClick={skipTemporarily} type="button" data-id="element-748">
                <SkipForwardIcon className="h-4 w-4" data-id="element-749" />
                Saltar por ahora
              </button> : null}
          </div>
        </div>
      </section>

      {activityType ? <WorkflowActionModal actionLabel={actionLabels[activityType] ?? label} actionType={activityType} businessName={prospect.businessName} channelUrl={channel.url} initialChannel={channel.id} messageTemplate={template} onClose={() => setActivityType(null)} onConfirm={completeActivity} data-id="element-750" /> : null}

      {showPostpone ? <Modal title={pendingFollowUp ? 'Editar seguimiento' : 'Programar seguimiento'} onClose={() => {
      if (!isSaving) setShowPostpone(false);
    }} data-id="element-751">
          <form onSubmit={pendingActivityRetry ? undefined : postpone} data-id="element-752">
            {pendingActivityRetry ? <p className="rounded-xl border border-amber-300/20 bg-amber-300/10 p-3 text-sm leading-6 text-amber-50" data-id="element-753">
                El seguimiento ya está guardado. Reintentá únicamente la
                actividad; no se volverá a crear un seguimiento.
              </p> : <>
                <p className="text-sm leading-6 text-slate-300" data-id="element-754">
                  {pendingFollowUp ? 'Actualizá la fecha, el canal o la nota del seguimiento pendiente.' : 'Recomendamos retomar en 48 horas. Ajustá la fecha si ya existe un acuerdo con el prospecto.'}
                </p>
                <label className="mt-5 block text-sm font-medium text-slate-200" data-id="element-755">
                  Fecha y hora recomendada
                  <input required type="datetime-local" value={postponeDueAt} onChange={event => setPostponeDueAt(event.target.value)} className="mt-2 w-full rounded-xl border border-white/10 bg-[#07111f] px-3 py-2.5 text-white" data-id="element-756" />
                </label>
                {pendingFollowUp || channel.id === 'manual' ? <label className="mt-4 block text-sm font-medium text-slate-200" data-id="element-757">
                    Canal del seguimiento
                    <select required value={postponeChannel} onChange={event => setPostponeChannel(event.target.value)} className="mt-2 w-full rounded-xl border border-white/10 bg-[#07111f] px-3 py-2.5 text-white" data-id="element-758">
                      <option value="" data-id="element-759">Elegí un canal</option>
                      <option value="whatsapp" data-id="element-760">WhatsApp</option>
                      <option value="instagram" data-id="element-761">Instagram</option>
                      <option value="facebook" data-id="element-762">Facebook</option>
                      <option value="email" data-id="element-763">Correo</option>
                      <option value="phone" data-id="element-764">Teléfono</option>
                    </select>
                  </label> : null}
                <label className="mt-4 block text-sm font-medium text-slate-200" data-id="element-765">
                  Nota del seguimiento
                  <textarea required rows={3} value={postponeReason} onChange={event => setPostponeReason(event.target.value)} placeholder="Ej. Retomar después de revisar la información enviada." className="mt-2 w-full resize-y rounded-xl border border-white/10 bg-[#07111f] px-3 py-2.5 text-white" data-id="element-766" />
                </label>
              </>}
            {error ? <p role="alert" className="mt-5 break-words rounded-lg border border-rose-400/20 bg-rose-400/10 p-3 text-xs leading-5 text-rose-100" data-id="element-767">
                {error}
              </p> : null}
            <div className="mt-6 flex justify-end gap-3" data-id="element-768">
              <button type="button" disabled={isSaving} onClick={() => setShowPostpone(false)} className="rounded-xl px-4 py-2.5 text-sm font-bold text-slate-300 disabled:opacity-60" data-id="element-769">
                Cancelar
              </button>
              {pendingActivityRetry ? <button type="button" disabled={isSaving} onClick={() => void retryActivity()} className="rounded-xl bg-amber-300 px-4 py-2.5 text-sm font-bold text-[#382500] disabled:opacity-60" data-id="element-770">
                  {isSaving ? 'Guardando actividad…' : 'Reintentar actividad'}
                </button> : <button type="submit" disabled={isSaving || !pendingFollowUp && channel.id === 'manual' && !postponeChannel} className="rounded-xl bg-cyan-300 px-4 py-2.5 text-sm font-bold text-[#06202a] disabled:opacity-60" data-id="element-771">
                  {isSaving ? pendingFollowUp ? 'Actualizando…' : 'Programando…' : pendingFollowUp ? 'Guardar cambios' : 'Programar'}
                </button>}
            </div>
          </form>
        </Modal> : null}
    </>;
}
function GuideItem({
  label,
  value
}: {
  label: string;
  value: string;
}) {
  return <div data-id="element-772">
      <p className="text-xs font-bold uppercase tracking-[.12em] text-slate-500" data-id="element-773">
        {label}
      </p>
      <p className="mt-1.5 text-sm leading-6 text-slate-200" data-id="element-774">{value}</p>
    </div>;
}
function Modal({
  title,
  onClose,
  children
}: {
  title: string;
  onClose: () => void;
  children: React.ReactNode;
}) {
  return <div className="fixed inset-0 z-50 flex items-end bg-slate-950/75 sm:items-center sm:justify-center sm:p-6" data-id="element-775">
      <section aria-label={title} aria-modal="true" className="w-full rounded-t-3xl border border-white/10 bg-[#0b1728] p-6 shadow-2xl sm:max-w-md sm:rounded-2xl" role="dialog" data-id="element-776">
        <div className="mb-4 flex items-center justify-between gap-4" data-id="element-777">
          <h2 className="text-lg font-bold text-white" data-id="element-778">{title}</h2>
          <button className="text-sm font-semibold text-cyan-200" onClick={onClose} type="button" data-id="element-779">
            Cerrar
          </button>
        </div>
        {children}
      </section>
    </div>;
}
function getRecommendedChannel(prospect: ProspectContact) {
  const whatsapp = prospect.whatsapp || prospect.phone;
  if (whatsapp) return {
    id: 'whatsapp' as const,
    label: 'WhatsApp',
    url: `https://wa.me/${whatsapp.replace(/\D/g, '')}`
  };
  if (prospect.instagram) return {
    id: 'instagram' as const,
    label: 'Instagram',
    url: prospect.instagram
  };
  if (prospect.facebook) return {
    id: 'facebook' as const,
    label: 'Facebook',
    url: prospect.facebook
  };
  if (prospect.email) return {
    id: 'email' as const,
    label: 'correo',
    url: `mailto:${prospect.email}`
  };
  return {
    id: 'manual' as const,
    label: 'el canal disponible',
    url: null
  };
}
function messageTemplate(businessName: string, contactName: string | null) {
  return `Hola${contactName ? ` ${contactName}` : ''}, soy [tu nombre]. Vi ${businessName} y quería compartirte una idea concreta para ayudarles con [beneficio relevante]. ¿Te parece si te envío la información por aquí?`;
}
function instructionFor(actionType: string, businessName: string) {
  if (actionType === 'wait_for_response') return 'Definí el próximo seguimiento sin insistir antes de tiempo.';
  if (['respond', 'qualify'].includes(actionType)) return 'Registrá el resultado de la conversación.';
  if (actionType === 'follow_up') return `Retomá el contacto con ${businessName} de forma breve y útil.`;
  return `Iniciá una conversación breve y personalizada con ${businessName}.`;
}
function rationaleFor(actionType: string) {
  if (actionType === 'wait_for_response') return 'Dar espacio y definir una fecha concreta evita que el prospecto se enfríe.';
  if (['respond', 'qualify'].includes(actionType)) return 'La respuesta recibida permite que el motor recomiende el siguiente paso.';
  return 'Un primer contacto claro abre oportunidades sin depender de una ficha completa.';
}
function howToFor(actionType: string, channel: string) {
  if (actionType === 'wait_for_response') return 'Usá la fecha sugerida de 48 horas o ajustala a lo acordado.';
  if (['respond', 'qualify'].includes(actionType)) return 'Elegí el resultado más cercano y agregá contexto solo si aporta valor.';
  return `Personalizá la plantilla, copiala y enviala por ${channel}. Después confirmá el envío.`;
}
function expectedResultFor(actionType: string) {
  if (actionType === 'wait_for_response') return 'Un seguimiento definido y trazable.';
  if (['respond', 'qualify'].includes(actionType)) return 'Una siguiente acción alineada con el interés real.';
  return 'Una conversación iniciada y una actividad registrada en el CRM.';
}
function primaryLabelFor(actionType: string) {
  if (['respond', 'qualify'].includes(actionType)) return 'Registrar respuesta';
  if (actionType === 'follow_up') return 'Enviar seguimiento';
  return 'Enviar y marcar mensaje enviado';
}
function effectiveWorkflowActionType(actionType: string | undefined) {
  return actionType === 'select_channel' || actionType === 'complete_information' ? 'send_message' : actionType;
}
function workflowStateLabel(state: NextAction['workflowState']) {
  if (state === 'waiting') return 'En espera';
  if (state === 'completed') return 'Proceso completado';
  if (state === 'blocked') return 'Proceso bloqueado';
  if (state === 'cancelled') return 'Proceso cancelado';
  return 'Proceso activo';
}
function formatDueAt(value: string) {
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return 'Fecha no disponible';
  return new Intl.DateTimeFormat('es-CR', {
    dateStyle: 'medium',
    timeStyle: 'short'
  }).format(date);
}
function defaultFollowUpDate() {
  return toDateTimeInputValue(new Date(Date.now() + 48 * 60 * 60 * 1000).toISOString());
}
function toDateTimeInputValue(value: string) {
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '';
  const offset = date.getTimezoneOffset() * 60000;
  return new Date(date.getTime() - offset).toISOString().slice(0, 16);
}
function channelLabel(channel: string) {
  const labels: Record<string, string> = {
    whatsapp: 'WhatsApp',
    instagram: 'Instagram',
    facebook: 'Facebook',
    email: 'Correo',
    phone: 'Teléfono'
  };
  return labels[channel] ?? channel;
}
function readWorkday(): WorkdayState | null {
  try {
    const stored = sessionStorage.getItem('titan-workday');
    if (!stored) return null;
    const parsed = JSON.parse(stored) as WorkdayState;
    return Array.isArray(parsed.ids) && Number.isInteger(parsed.index) ? parsed : null;
  } catch {
    sessionStorage.removeItem('titan-workday');
    return null;
  }
}
async function copyToClipboard(value: string, setNotice: (value: string) => void) {
  try {
    await navigator.clipboard.writeText(value);
    setNotice('Mensaje copiado. Personalizalo antes de enviarlo.');
  } catch {
    setNotice('No se pudo copiar automáticamente. Podés seleccionar el texto manualmente.');
  }
}
function isFollowUpActivityInsertError(error: unknown): error is FollowUpScheduleError {
  return error instanceof FollowUpScheduleError && error.diagnostic.stage === 'follow_up_activity_insert';
}
function messageFrom(error: unknown, fallback: string) {
  return error instanceof Error ? error.message : fallback;
}
function navigate(path: string) {
  window.history.pushState({}, '', path);
  window.dispatchEvent(new PopStateEvent('popstate'));
}